import { useTranslation } from "react-i18next";
import IconComponent from "@CubeComponents/icons";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { DateTimePickerComponent } from "@syncfusion/ej2-react-calendars";
import { NumericTextBoxComponent } from "@syncfusion/ej2-react-inputs";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { DeleteIcon, PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import { getIconFromFieldType } from "./constants";
import { presetSectionsCodeObject } from "../../../../../../../helpers";
import { addValidationToForms, formSectionType } from "../../../../CreateInspectionAndProgress/constants";
import { v4 as uuid } from "uuid";
import { useSelector } from "react-redux";
import _ from "lodash";
import FormQuestionFields from "../../../../CreateInspectionAndProgress/questionFields";
import useForms from "../../../../CreateInspectionAndProgress/useForms";
import { forwardRef, useEffect } from "react";
import useFormValidator from "../../../../FormValidation/useFormValidator";
import CheckBoxComponent from "../../../../../cubeComponent/Mui_Form/CheckBox";
import { Box } from "@mui/material";

interface FormPreviewProps {
  formData?: any;
  setFormData?: any;
  disabled?: boolean;
  allowPadding?: boolean;
  handleData?: any;
  allowBorder?: boolean;
  headingFontSize?: string;
  defaultSection?: any;
  showInGrid?: boolean;
  showInSidebar?: boolean;
  fullWidthInput?: boolean;
  removeIcons?: boolean;
  showNewFields?: boolean;
  showAnswersOnly?: boolean;
  handleSaveCustomFields?: any;
  setUpdateTemplateAfterUse?: any;
  formValidator?: any;
  showHeaderCheckBox?: boolean;
  selectedSections?: any;
  handleSectionSelection?: any;
  defaultExpanded?: boolean;
  spaceId?: any;
}
const FormPreview = forwardRef(
  ({
    formData = { template: [] },
    setFormData,
    disabled = true,
    allowPadding = true,
    handleData,
    allowBorder = true,
    headingFontSize = "text-xl",
    defaultSection,
    showInGrid = false,
    showInSidebar = false,
    fullWidthInput = false,
    removeIcons = false,
    showNewFields = false,
    showAnswersOnly = false,
    handleSaveCustomFields,
    setUpdateTemplateAfterUse,
    formValidator,
    showHeaderCheckBox = false,
    selectedSections = [],
    handleSectionSelection = null,
    defaultExpanded = true,
    spaceId = null,
  }: FormPreviewProps) => {
    const { t } = useTranslation();
    const tempValidator = useFormValidator({});
    if (!formValidator) {
      formValidator = tempValidator;
    }
    const showFullInput = showInGrid || showInSidebar || fullWidthInput;
    const userDetails: any = useSelector((state: any) => state.userDetails);
    const {
      handleInputChange,
      handleURLModalState,
      isFileUploading,
      handleFileUploadChange,
      removeUserFromList,
      saveEditedImage,
      handleRemoveUploadedFile,
      openAddUserModal,
      setURLData,
      showUrlModal,
      handleSaveUrlData,
      showGeoLocationModal,
      setShowGeoLocationModal,
      handleGeoLocationModalState,
    } = useForms({});

    const showFields = (fieldType: any) => {
      if (["users", "fileUpload", "image"].includes(fieldType)) {
        return showNewFields;
      }
      return true;
    };

    useEffect(() => {
      addValidationToForms({
        formValidator,
        template: formData?.template,
      });
    }, []);

    const handleCustomFieldData = ({
      eachField,
      answerValue,
      fieldIndex,
      sectionIndex,
      tableRowIndex,
      source,
    }: {
      eachField: any;
      answerValue: any;
      fieldIndex: number;
      sectionIndex: number;
      tableRowIndex: number;
      source: string;
    }) => {
      const { _id } = eachField;
      const modifiedTemplate = _.cloneDeep(formData?.template);
      if (source == formSectionType.TABLE_SECTION) {
        const modifiedTableDataSource = modifiedTemplate[sectionIndex].tableDataSource.map(
          (row: any, index: number) => {
            if (index == tableRowIndex) {
              row[_id] = {
                createdDate: Date.now(),
                createdBy: userDetails._id,
                value: answerValue,
              };
            }
            return row;
          },
        );
        modifiedTemplate[sectionIndex].tableDataSource = modifiedTableDataSource;

        setUpdateTemplateAfterUse((prev: any) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...prev.CONFIGURE_SECTION,
            {
              fieldType: "tableDataSource",
              tableDataSource: modifiedTableDataSource,
              sectionId: formData.template[sectionIndex]._id,
            },
          ],
        }));
      } else if (modifiedTemplate[sectionIndex].questionFields[fieldIndex].answer) {
        modifiedTemplate[sectionIndex].questionFields[fieldIndex].answer = {
          ...modifiedTemplate[sectionIndex].questionFields[fieldIndex].answer,
          value: answerValue,
        };
      } else {
        modifiedTemplate[sectionIndex].questionFields[fieldIndex].answer = {
          value: answerValue,
        };
      }
      if (handleData) {
        handleData(modifiedTemplate);
      }

      if (setFormData) {
        setFormData((prev: any) => ({
          ...prev,
          template: modifiedTemplate,
        }));
      }
      if (handleSaveCustomFields) {
        const fieldsData = [
          {
            _id,
            value: answerValue,
          },
        ];
        handleSaveCustomFields(fieldsData, modifiedTemplate);
      }
    };

    const handleSectionSelect = ({ args, sectionId }: { args: any; sectionId: any }) => {
      if (handleSectionSelection) {
        handleSectionSelection({ isChecked: args.target.checked, sectionId });
      }
    };

    const previewSectionHeader = (index: number, data: any, isDefaultSection?: boolean) => (
      <div
        className={`flex justify-between w-full ${allowPadding ? "ps-4 pe-4" : ""} py-3 ${
          allowBorder ? "border-b border-button_light" : ""
        } items-center`}
      >
        <Box onClick={(e: any) => e.stopPropagation()} className="flex gap-2 items-center">
          {showHeaderCheckBox && (
            <CheckBoxComponent
              onChange={(args) => handleSectionSelect({ args, sectionId: data._id })}
              checked={selectedSections.includes(data._id)}
            />
          )}
          <span className={`${headingFontSize} font-semibold`}>{t(data.sectionHeading)}</span>
          {isDefaultSection && (
            <span className="rounded-full text-xxs bg-gray-500 text-white px-2 py-0.5">{t("Default")}</span>
          )}
        </Box>
        <span>
          <IconComponent name="arrowDown" cssClass="cursor-pointer" />
        </span>
      </div>
    );

    const gridPreviewSectionContent = (data: any, sectionIndex: number) => (
      <div className="grid grid-cols-2 gap-20 w-full px-2">
        {data.questionFields?.map(
          (fields: any, index: number) =>
            showFields(fields.fieldType) && (
              <div key={fields._id} className="flex">
                <div className="flex">
                  <div className="text-base font-semibold" data-testid="projectId">
                    {fields.questionValue}
                  </div>
                  {fields.isMandatory && <span className="text-2xl align-middle font-light text-red-500">*</span>}
                </div>
                <div className="w-3/4 ms-auto">
                  <FormQuestionFields
                    formValidator={formValidator}
                    showAnswersOnly={showAnswersOnly}
                    eachField={fields}
                    fieldIndex={index}
                    sectionIndex={sectionIndex}
                    section={data}
                    handleCustomFieldData={handleCustomFieldData}
                    disabled={disabled}
                    width={showFullInput ? "100%" : "30%"}
                    formData={{ selectedForm: formData }}
                    handleInputChange={handleInputChange}
                    handleURLModalState={handleURLModalState}
                    isFileUploading={isFileUploading}
                    handleFileUploadChange={handleFileUploadChange}
                    removeUserFromList={removeUserFromList}
                    saveEditedImage={saveEditedImage}
                    handleRemoveUploadedFile={handleRemoveUploadedFile}
                    openAddUserModal={openAddUserModal}
                    handleSaveUrlData={handleSaveUrlData}
                    showUrlModal={showUrlModal}
                    setURLData={setURLData}
                    spaceId={spaceId}
                    showGeoLocationModal={showGeoLocationModal}
                    setShowGeoLocationModal={setShowGeoLocationModal}
                    handleGeoLocationModalState={handleGeoLocationModalState}
                  />
                </div>
              </div>
            ),
        )}
      </div>
    );

    const sidebarPreviewSectionContent = (data: any, sectionIndex: number) => (
      <div className="flex flex-col gap-4 w-full px-2">
        {data.questionFields?.map(
          (fields: any, index: number) =>
            showFields(fields.fieldType) && (
              <div key={fields._id} className="flex">
                <div className="flex gap-1 flex-1">
                  <div className="text-sm font-semibold" data-testid="projectId">
                    {fields.questionValue}
                  </div>
                  {fields.isMandatory && <span className="text-2xl align-middle font-light text-red-500">*</span>}
                </div>
                <div className="flex-1">
                  <FormQuestionFields
                    formValidator={formValidator}
                    showAnswersOnly={showAnswersOnly}
                    eachField={fields}
                    fieldIndex={index}
                    sectionIndex={sectionIndex}
                    section={data}
                    handleCustomFieldData={handleCustomFieldData}
                    disabled={disabled}
                    width={showFullInput ? "100%" : "30%"}
                    formData={{ selectedForm: formData }}
                    handleInputChange={handleInputChange}
                    handleURLModalState={handleURLModalState}
                    isFileUploading={isFileUploading}
                    handleFileUploadChange={handleFileUploadChange}
                    removeUserFromList={removeUserFromList}
                    saveEditedImage={saveEditedImage}
                    handleRemoveUploadedFile={handleRemoveUploadedFile}
                    openAddUserModal={openAddUserModal}
                    spaceId={spaceId}
                    handleSaveUrlData={handleSaveUrlData}
                    showUrlModal={showUrlModal}
                    setURLData={setURLData}
                    showGeoLocationModal={showGeoLocationModal}
                    setShowGeoLocationModal={setShowGeoLocationModal}
                    handleGeoLocationModalState={handleGeoLocationModalState}
                  />
                </div>
              </div>
            ),
        )}
      </div>
    );

    const defaultPreviewSectionContent = (data: any, sectionIndex: number) => (
      <div className={`flex flex-col gap-8 ${allowPadding ? "ps-3" : ""}`}>
        {data.questionFields?.map(
          (fields: any, index: number) =>
            showFields(fields.fieldType) && (
              <div key={fields._id} className="flex flex-col gap-3">
                <div className="flex gap-3">
                  {!removeIcons && (
                    <span className="w-6 flex justify-start items-center flex-col gap-1">
                      <span>{getIconFromFieldType(fields.fieldType)}</span>
                    </span>
                  )}
                  <span className="flex w-11/12 flex-col break-words gap-1">
                    <span className="flex">
                      {/* <span className="font-semibold">
                        {fields.questionValue}
                      </span> */}
                      <div className="text-base font-semibold" data-testid="projectId">
                        {`${fields.questionValue}`}{" "}
                      </div>
                      {fields.isMandatory && <span className="text-2xl align-middle font-light text-red-500">*</span>}
                    </span>
                    {fields.fieldInstruction && (
                      <span className="text-gray-400 w-full text-xs">
                        {fields.fieldInstruction ? `instructions : ${fields.fieldInstruction}` : ""}
                      </span>
                    )}
                  </span>
                </div>
                <div className={allowPadding ? "ps-8" : ""}>
                  {fields.isVaries && <span className="text-red-500 italic me-1">{t("Varies")}</span>}
                  <FormQuestionFields
                    formValidator={formValidator}
                    showAnswersOnly={showAnswersOnly}
                    eachField={fields}
                    fieldIndex={index}
                    sectionIndex={sectionIndex}
                    section={data}
                    handleCustomFieldData={handleCustomFieldData}
                    disabled={disabled}
                    width={showFullInput ? "100%" : "30%"}
                    formData={{ selectedForm: formData }}
                    handleInputChange={handleInputChange}
                    handleURLModalState={handleURLModalState}
                    isFileUploading={isFileUploading}
                    handleFileUploadChange={handleFileUploadChange}
                    removeUserFromList={removeUserFromList}
                    saveEditedImage={saveEditedImage}
                    handleRemoveUploadedFile={handleRemoveUploadedFile}
                    openAddUserModal={openAddUserModal}
                    handleSaveUrlData={handleSaveUrlData}
                    showUrlModal={showUrlModal}
                    setURLData={setURLData}
                    spaceId={spaceId}
                    showGeoLocationModal={showGeoLocationModal}
                    setShowGeoLocationModal={setShowGeoLocationModal}
                    handleGeoLocationModalState={handleGeoLocationModalState}
                  />
                </div>
              </div>
            ),
        )}
      </div>
    );

    const handleAddTableRowClick = ({ sectionIndex }: { sectionIndex: number }) => {
      if (setFormData) {
        const newObjRow: any = {
          rowId: uuid(),
          isPreFilledRow: true,
        };
        formData.template[sectionIndex].questionFields.map((field: any) => {
          newObjRow[field._id] = {};
        });
        const modifiedTemplate = [...formData.template];

        let newTableDataSource = [];
        if (modifiedTemplate[sectionIndex].tableDataSource) {
          newTableDataSource = [...modifiedTemplate[sectionIndex].tableDataSource, newObjRow];
        } else {
          newTableDataSource = [newObjRow];
        }
        modifiedTemplate[sectionIndex].tableDataSource = newTableDataSource;

        setFormData((prev: any) => ({
          ...prev,
          template: modifiedTemplate,
        }));

        setUpdateTemplateAfterUse((prev: any) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...prev.CONFIGURE_SECTION,
            {
              fieldType: "tableDataSource",
              tableDataSource: newTableDataSource,
              sectionId: formData.template[sectionIndex]._id,
            },
          ],
        }));
      }
    };

    const handleDeleteTableRow = ({ sectionIndex, rowIndex }: { sectionIndex: number; rowIndex: number }) => {
      if (setFormData) {
        const modifiedTemplate = [...formData.template];

        const dataSource = modifiedTemplate[sectionIndex].tableDataSource;

        dataSource.splice(rowIndex, 1);

        modifiedTemplate[sectionIndex].tableDataSource = [...dataSource];

        setFormData((prev: any) => ({
          ...prev,
          template: modifiedTemplate,
        }));

        setUpdateTemplateAfterUse((prev: any) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...prev.CONFIGURE_SECTION,
            {
              fieldType: "tableDataSource",
              tableDataSource: [...dataSource],
              sectionId: formData.template[sectionIndex]._id,
            },
          ],
        }));
      }
    };

    const getTableSectionGrid = (data: any, sectionIndex: number) => {
      const dataSource = data.tableDataSource || [];
      return (
        <span className="overflow-auto flex flex-col gap-4 w-full">
          <span>
            {setFormData && (
              <ButtonComponent
                disabled={!data.allowPreDefineRowValues}
                onClick={() => handleAddTableRowClick({ sectionIndex })}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              >
                {t("Add Row")}
              </ButtonComponent>
            )}
          </span>
          <table className="w-full overflow-auto flex flex-col h-full">
            <tr className="h-full w-full flex justify-normal text-start">
              {dataSource.length > 1 && (
                <th className="p-4 text-sm border-2 w-14 bg-button_light border-button_light">.....</th>
              )}
              <th className="p-4 text-sm border-2 w-14 bg-button_light border-button_light">{t("S. No.")}</th>
              {data.questionFields.map((field: any) => (
                // <ToolTipComponent content={field.questionValue} position="top">
                <th
                  key={field._id}
                  className="p-4 items-center flex text-sm border-2 border-button_light w-full min-w-60 bg-button_light whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  {field.isMandatory && <span className="text-2xl font-light text-red-500">*</span>}
                  <span>{`${field.questionValue}`}</span>
                </th>
                //  </ToolTipComponent>
              ))}
            </tr>
            {dataSource.map((obj: any, rowIndex: number) => (
              <tr key={obj.rowId} className="h-full w-full flex justify-normal text-start">
                {dataSource.length > 1 && (
                  <td className="p-4 min-w-14 max-w-40 border-2 border-button_light">
                    <IconComponent
                      onClick={() =>
                        handleDeleteTableRow({
                          sectionIndex,
                          rowIndex,
                        })
                      }
                      cssClass="cursor-pointer"
                      name="delete"
                    />
                  </td>
                )}
                <td className="p-4 min-w-14 max-w-40 border-2 border-button_light">{rowIndex + 1}</td>
                {data.questionFields.map((field: any, fieldIndex: number) => (
                  <td
                    key={field._id}
                    className="p-4 min-w-60 max-h-60 overflow-auto w-full border-2 border-button_light"
                  >
                    <FormQuestionFields
                      formValidator={formValidator}
                      showAnswersOnly={showAnswersOnly}
                      eachField={field}
                      fieldIndex={fieldIndex}
                      sectionIndex={sectionIndex}
                      section={data}
                      handleCustomFieldData={handleCustomFieldData}
                      disabled={!(field.allowPreDefinedColValues && !!setFormData && data.allowPreDefineRowValues)}
                      tableRowId={data.tableDataSource[rowIndex]?.rowId}
                      tableRowIndex={rowIndex}
                      width="100%"
                      source={formSectionType.TABLE_SECTION}
                      formData={{ selectedForm: formData }}
                      handleInputChange={handleInputChange}
                      handleURLModalState={handleURLModalState}
                      isFileUploading={isFileUploading}
                      handleFileUploadChange={handleFileUploadChange}
                      removeUserFromList={removeUserFromList}
                      saveEditedImage={saveEditedImage}
                      handleRemoveUploadedFile={handleRemoveUploadedFile}
                      openAddUserModal={openAddUserModal}
                      handleSaveUrlData={handleSaveUrlData}
                      showUrlModal={showUrlModal}
                      setURLData={setURLData}
                      spaceId={spaceId}
                      showGeoLocationModal={showGeoLocationModal}
                      setShowGeoLocationModal={setShowGeoLocationModal}
                      handleGeoLocationModalState={handleGeoLocationModalState}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </table>
        </span>
      );
    };

    const previewSectionContent = (sectionIndex: number, data: any) => {
      if (
        [
          presetSectionsCodeObject.SECTION_TYPE_QUANTITY,
          presetSectionsCodeObject.SECTION_TYPE_FORM,
          presetSectionsCodeObject.SECTION_TYPE_REFERENCE,
          presetSectionsCodeObject.SECTION_TYPE_WBS_ITEMS,
          presetSectionsCodeObject.SECTION_TYPE_REVIEWS,
        ].includes(data.sectionCode)
      ) {
        return (
          <div className="flex items-center gap-2">
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} disabled={disabled}>
              <div className="flex items-center">
                <PlusIcon color="var(--system-colour-37" />
                <span>{t("Add")}</span>
              </div>
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} disabled={disabled}>
              <div className="flex items-center">
                <DeleteIcon color="var(--system-colour-37" />
                <span>{t("Remove")}</span>
              </div>
            </ButtonComponent>
          </div>
        );
      }
      if (data.sectionHeading == "Tasks" && data.sectionCode) {
        return (
          <div className="flex flex-col gap-5 p-3">
            <span className="flex gap-3 items-center">
              {t("Actual Start Date")}
              <DateTimePickerComponent
                name="actualStartDate"
                width="400px"
                disabled
                id="actualStartDate"
                placeholder={t("Select Date")}
              />
            </span>
            <span className="flex gap-3 items-center">
              {t("Actual End Date")}
              <DateTimePickerComponent
                disabled
                name="actualEndDate"
                width="400px"
                id="actualEndDate"
                placeholder={t("Select Date")}
              />
            </span>
            <span className="flex gap-3 items-center">
              {t("Progress %")}
              <NumericTextBoxComponent width="400px" disabled />
            </span>
            <span className="flex gap-3 items-center">
              {t("Remaining Duration")}
              <NumericTextBoxComponent width="400px" disabled />
            </span>
            <div className="flex items-center gap-2">
              <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} disabled>
                <div className="flex items-center">
                  <PlusIcon color="var(--system-colour-37)" />
                  <span>{t("Add")}</span>
                </div>
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} disabled>
                <div className="flex items-center">
                  <DeleteIcon color="var(--system-colour-37)" />
                  <span>{t("Remove")}</span>
                </div>
              </ButtonComponent>
            </div>
          </div>
        );
      }

      if (data.sectionHeading == "Milestones" && data.sectionCode) {
        return (
          <div className="flex flex-col gap-5">
            <div className="flex gap-5 p-3 items-center">
              {t("Milestone Completion Date")}
              <DateTimePickerComponent
                width="400px"
                name="MilestoneCompletionDate"
                disabled
                id="MilestoneCompletionDate"
                placeholder={t("Select Date")}
              />
            </div>
            <div className="flex gap-3 items-center p-3">
              {t("Milestone Amount")}
              <NumericTextBoxComponent
                name="MilestoneAmount"
                width="400px"
                disabled
                id="MilestoneAmount"
                placeholder={t("Enter Amount")}
              />
            </div>
          </div>
        );
      }
      if (data.sectionCode == presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION) {
        return getTableSectionGrid(data, sectionIndex);
      }
      if (showInGrid) {
        return gridPreviewSectionContent(data, sectionIndex);
      }
      if (showInSidebar) {
        return sidebarPreviewSectionContent(data, sectionIndex);
      }
      return defaultPreviewSectionContent(data, sectionIndex);
    };

    return (
      <div className="flex flex-col w-full h-full p-1 gap-2 overflow-auto" id="formData">
        {defaultSection && (
          <Accordion
            defaultExpanded={defaultExpanded}
            key="deafult-section-accordian"
            border={allowBorder ? "1px solid var(--primary-blue)" : ""}
            borderRadius="10px"
            style={{ width: "100%" }}
          >
            <AccordionSummary>{previewSectionHeader(formData.template?.length, defaultSection, true)}</AccordionSummary>
            <AccordionDetails>{previewSectionContent(formData?.template?.length, defaultSection)}</AccordionDetails>
          </Accordion>
        )}
        {formData?.template?.length > 0 &&
          formData?.template.map((data: any, index: number) => (
            <Accordion
              defaultExpanded={defaultExpanded}
              key={data._id}
              border={allowBorder ? "1px solid var(--primary-blue)" : ""}
              borderRadius="10px"
              style={{ width: "100%" }}
              sx={
                !allowPadding && {
                  "& .MuiAccordionDetails-root": {
                    height: "100%",
                    padding: "0",
                  },
                }
              }
            >
              <AccordionSummary>{previewSectionHeader(index, data)}</AccordionSummary>
              <AccordionDetails>{previewSectionContent(index, data)}</AccordionDetails>
            </Accordion>
          ))}
      </div>
    );
  },
);
FormPreview.displayName = "FormPreview";
export default FormPreview;
